// rounds/configuration — deadline extension
import { z } from "zod";

/** The backend's `reason` is `CharField(max_length=255)`, and CharField rejects a blank string. */
export const DEADLINE_REASON_MAX_LENGTH = 255;

/**
 * Extending a round's closing date.
 *
 * `closing_at` is a `datetime-local` wall-time string, like every other datetime in the builder;
 * conversion to an instant happens at the service boundary.
 *
 * The rule that the new date must be LATER cannot live here — it needs the round's current deadline,
 * which is data rather than schema. See {@link deadlineExtensionSchema}.
 */
export const DeadlineExtensionSchema = z.object({
  closing_at: z.string().min(1, "round-create.errors.datetime-required"),
  reason: z
    .string()
    .trim()
    .min(1, "round-create.errors.reason-required")
    .max(DEADLINE_REASON_MAX_LENGTH, "round-create.errors.reason-too-long"),
});

export type DeadlineExtension = z.infer<typeof DeadlineExtensionSchema>;

/**
 * The full contract for one round: the base schema plus "strictly later than the current deadline",
 * which is exactly what `extend_round_deadline` enforces (`deadline_must_be_later`, 400).
 *
 * A factory rather than a bare refine because the comparison needs the round's present `closing_at`.
 * Mirroring the server rule here means the administrator is told inline instead of by a toast after
 * a round trip — the server stays authoritative either way.
 *
 * `currentClosingAt` is the ISO instant from the round payload; the form value is wall time, so both
 * go through `Date` rather than being compared as strings.
 */
export function deadlineExtensionSchema(currentClosingAt: string) {
  return DeadlineExtensionSchema.refine(
    (value) => {
      const next = new Date(value.closing_at).getTime();
      const current = new Date(currentClosingAt).getTime();
      // An unparsable pair is left to the field's own required rule rather than reported twice.
      if (Number.isNaN(next) || Number.isNaN(current)) return true;
      return next > current;
    },
    {
      message: "round-create.errors.deadline-must-be-later",
      path: ["closing_at"],
    }
  );
}

/** Reopening a CLOSED round. Same wire shape as an extension — a later `closing_at` and a `reason`. */
export type ReopenRound = DeadlineExtension;

/**
 * The full contract for reopening: the base schema plus "strictly later than NOW", which is what
 * `reopen_intake_round` enforces (`deadline_must_be_later`, 409). A CLOSED round's own `closing_at`
 * is necessarily already in the past, so — unlike the extension's rule — comparing against the
 * PREVIOUS deadline would accept any future-ish typo. The server's real gate is "later than both",
 * so this mirrors that: `max(now, currentClosingAt)`.
 */
export function reopenRoundSchema(currentClosingAt: string) {
  return DeadlineExtensionSchema.refine(
    (value) => {
      const next = new Date(value.closing_at).getTime();
      const floor = Math.max(Date.now(), new Date(currentClosingAt).getTime());
      if (Number.isNaN(next) || Number.isNaN(floor)) return true;
      return next > floor;
    },
    {
      message: "round-create.errors.deadline-must-be-later",
      path: ["closing_at"],
    }
  );
}
